import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultBranding = {
  logo_url: null,
  primary_color: '#3b82f6',
  accent_color: '#f59e0b',
  app_name: 'Road to Glory',
  custom_domain: null
};

const BrandingContext = createContext({
  branding: defaultBranding,
  updateBranding: async () => {},
  uploadLogo: async () => {},
  loading: false
});

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider = ({ children }) => {
  const { user } = useAuth();
  const [branding, setBranding] = useState(defaultBranding);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchBranding();
    } else {
      setBranding(defaultBranding);
    }
  }, [user]);

  // Apply branding colors to CSS variables
  useEffect(() => {
    if (branding.primary_color) {
      document.documentElement.style.setProperty('--brand-primary', branding.primary_color);
    }
    if (branding.accent_color) {
      document.documentElement.style.setProperty('--brand-accent', branding.accent_color);
    }
  }, [branding]);

  const fetchBranding = async () => {
    try {
      const res = await axios.get(`${API}/branding/settings`);
      setBranding(res.data.settings || defaultBranding);
    } catch (error) {
      console.error('Error fetching branding:', error);
    }
  };

  const updateBranding = async (settings) => {
    setLoading(true);
    try {
      const res = await axios.put(`${API}/branding/settings`, settings);
      setBranding(res.data.settings);
      return { success: true };
    } catch (error) {
      console.error('Error updating branding:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to update branding' 
      };
    } finally {
      setLoading(false);
    }
  };

  const uploadLogo = async (file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/branding/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBranding(prev => ({ ...prev, logo_url: res.data.logo_url }));
      return { success: true, logo_url: res.data.logo_url };
    } catch (error) {
      console.error('Error uploading logo:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to upload logo' 
      };
    } finally {
      setLoading(false);
    }
  };

  return (
    <BrandingContext.Provider value={{
      branding,
      updateBranding,
      uploadLogo,
      loading
    }}>
      {children}
    </BrandingContext.Provider>
  );
};
